import React, { useState } from 'react';
import { OrganisationResponse, UpsertOrganisationForm, UpsertOrganisationRequest } from '../../../types/OrganisationInterfaces';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import { OrganisationValidations } from '../../../validations/OrganisationValidations';
import { OrganisationService } from '../../../services/OrganisationService';
import { OrganisationRoles } from '../../../enums/OrganisationRoles';
import Box from '@mui/material/Box';
import ErrorComponent from '../shared/ErrorComponent';
import Grid from '@mui/material/Grid';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { GeneralHelper } from '../../../helpers/GeneralHelper';
import Typography from '@mui/material/Typography';
import { Button, Checkbox, FormControlLabel } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import FormHelperText from '@mui/material/FormHelperText';
import ButtonLoadingComponent from '../shared/ButtonLoadingComponent';
import Paper from '@mui/material/Paper';

interface UpsertOrganisationComponentProps {
  selectedOrganisationId?: number;
  selectedOrganisation?: OrganisationResponse;
}

const UpsertOrganisationComponent = (props: UpsertOrganisationComponentProps) => {
  const { selectedOrganisation, selectedOrganisationId } = props;

  const [isUpsertingOrganisation, setIsUpsertingOrganisation] = useState<boolean>(false);
  const [upsertError, setUpsertError] = useState<string | null>(null);

  const defaultForm: UpsertOrganisationForm = {
    name: '',
    websiteUrl: '',
    roles: [],
    accreditedBy: null,
    accreditationDate: null,
    addressLine1: null,
    addressLine2: null,
    city: null,
    state: null,
    postalCode: null,
    embeddedMap: ''
  };

  if (selectedOrganisation) {
    defaultForm.name = selectedOrganisation.name;
    defaultForm.websiteUrl = selectedOrganisation.websiteUrl;

    if (selectedOrganisation.solarInstaller) {
      defaultForm.roles.push(OrganisationRoles.SOLAR_INSTALLER);
      defaultForm.accreditedBy = selectedOrganisation.solarInstaller.accreditedByMemberId;
      defaultForm.accreditationDate = selectedOrganisation.solarInstaller.accreditationDate;
    }

    if (selectedOrganisation.solarConsumer) {
      defaultForm.roles.push(OrganisationRoles.SOLAR_CONSUMER);
      defaultForm.embeddedMap = selectedOrganisation.solarConsumer.embeddedMap;
    }

    if (selectedOrganisation.address) {
      defaultForm.addressLine1 = selectedOrganisation.address.addressLineOne;
      defaultForm.addressLine2 = selectedOrganisation.address.addressLineTwo;
      defaultForm.city = selectedOrganisation.address.city;
      defaultForm.state = selectedOrganisation.address.state;
      defaultForm.postalCode = selectedOrganisation.address.postalCode;
    }
  }

  const history = useHistory();

  const formik = useFormik({
    initialValues: defaultForm,
    validationSchema: OrganisationValidations.upsertOrganisationValidation(),
    onSubmit: async values => {
      setIsUpsertingOrganisation(true);
      try {
        const formData: UpsertOrganisationRequest = {
          id: selectedOrganisationId,
          name: values.name,
          websiteUrl: values.websiteUrl,
          roles: values.roles,
          accreditedBy: values.accreditedBy,
          accreditationDate: values.accreditationDate,
          addressLineOne: values.addressLine1,
          addressLineTwo: values.addressLine2,
          city: values.city,
          state: values.state,
          postalCode: values.postalCode,
          embeddedMap: values.embeddedMap
        };

        // 828 const organisationId: number = await OrganisationService.upsertOrganisation(formData);
        // 828 history.push(`/app/organisations/${organisationId}`);
        console.log(formData)
      } catch (error) {
        setUpsertError(error.message);
      }
      setIsUpsertingOrganisation(false);
    }
  });

  const handleCheckboxClick = (role: OrganisationRoles): void => {
    if (formik.values.roles.find(x => x === role) === undefined) {
      formik.values.roles.push(role);
      console.log('new roles', formik.values.roles);
      formik.setFieldValue('roles', formik.values.roles);
    } else {
      formik.setFieldValue('roles', formik.values.roles.filter(r => r !== role));
    }
  };

  const hasSolarInstallerRole = (): boolean => {
    return formik.values.roles.find(role => role === OrganisationRoles.SOLAR_INSTALLER) !== undefined;
  };

  const hasSolarConsumerRole = (): boolean => {
    return formik.values.roles.find(role => role === OrganisationRoles.SOLAR_CONSUMER) !== undefined;
  };

  return (
    <Paper>
      <Box sx={{ p: 4 }}>
        {upsertError && <ErrorComponent message={upsertError} />}
        <form autoComplete={'off'} autoCorrect={'off'} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item md={6}>
              <TextField
                label="Name"
                variant="outlined"
                {...formik.getFieldProps('name')}
                error={GeneralHelper.formFieldHasError(formik, 'name')}
                sx={{ width: '100%' }}
                helperText={formik.errors.name}
              />
            </Grid>
            <Grid item md={6}>
              <TextField
                label="Website URL"
                variant="outlined"
                type={'url'}
                {...formik.getFieldProps('websiteUrl')}
                error={GeneralHelper.formFieldHasError(formik, 'websiteUrl')}
                sx={{ width: '100%' }}
                helperText={formik.errors.websiteUrl}
              />
            </Grid>
            {selectedOrganisationId &&
	            <Grid item md={12}>
		            <Typography variant={'body2'}>Roles</Typography>
		            <Grid container>
			            <Grid item>
				            <FormControlLabel
					            checked={hasSolarInstallerRole()}
					            control={<Checkbox onClick={() => handleCheckboxClick(OrganisationRoles.SOLAR_INSTALLER)} />}
					            label="Solar Installer"
				            />
			            </Grid>
			            <Grid item>
				            <FormControlLabel
					            checked={hasSolarConsumerRole()}
					            control={<Checkbox onClick={() => handleCheckboxClick(OrganisationRoles.SOLAR_CONSUMER)} />}
					            label="Solar Consumer"
				            />
			            </Grid>
		            </Grid>
	            </Grid>
            }

            {hasSolarInstallerRole() && (
              <Grid item md={12}>
                <Typography variant={'subtitle1'}>Solar Installer</Typography>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item md={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        inputFormat="dd-MMM-yyyy"
                        renderInput={(props: JSX.IntrinsicAttributes & TextFieldProps) => <TextField {...props} className={'w-100'} />}
                        label="Start date"
                        value={formik.values.accreditationDate}
                        onChange={(newValue: any) => {
                          formik.setFieldValue('accreditationDate', newValue);
                        }}
                      />
                    </LocalizationProvider>
                    <FormHelperText>{formik.errors.accreditationDate}</FormHelperText>
                  </Grid>
                  <Grid item md={6}>
                    <TextField
                      label="Accredited By (Member ID)"
                      variant="outlined"
                      type={'number'}
                      {...formik.getFieldProps('accreditedBy')}
                      error={GeneralHelper.formFieldHasError(formik, 'accreditedBy')}
                      sx={{ width: '100%' }}
                      helperText={formik.errors.accreditedBy}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            {hasSolarConsumerRole() && (
              <Grid item md={12}>
                <Typography variant={'subtitle1'}>Solar Consumer</Typography>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item md={6}>
                    <TextField
                      label="Address Line 1"
                      variant="outlined"
                      type={'text'}
                      {...formik.getFieldProps('addressLine1')}
                      error={GeneralHelper.formFieldHasError(formik, 'addressLine1')}
                      sx={{ width: '100%' }}
                      helperText={formik.errors.addressLine1}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <TextField
                      label="Address Line 2"
                      variant="outlined"
                      type={'text'}
                      {...formik.getFieldProps('addressLine2')}
                      error={GeneralHelper.formFieldHasError(formik, 'addressLine2')}
                      sx={{ width: '100%' }}
                      helperText={formik.errors.addressLine2}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <TextField
                      label="City"
                      variant="outlined"
                      type={'text'}
                      {...formik.getFieldProps('city')}
                      error={GeneralHelper.formFieldHasError(formik, 'city')}
                      sx={{ width: '100%' }}
                      helperText={formik.errors.city}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <TextField
                      label="State"
                      variant="outlined"
                      type={'text'}
                      {...formik.getFieldProps('state')}
                      error={GeneralHelper.formFieldHasError(formik, 'state')}
                      sx={{ width: '100%' }}
                      helperText={formik.errors.state}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <TextField
                      label="Postal code"
                      variant="outlined"
                      type={'text'}
                      {...formik.getFieldProps('postalCode')}
                      error={GeneralHelper.formFieldHasError(formik, 'postalCode')}
                      sx={{ width: '100%' }}
                      helperText={formik.errors.postalCode}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <TextField
                      label="Embedded Map URL"
                      variant="outlined"
                      type={'url'}
                      {...formik.getFieldProps('embeddedMap')}
                      error={GeneralHelper.formFieldHasError(formik, 'embeddedMap')}
                      sx={{ width: '100%' }}
                      helperText={formik.errors.embeddedMap}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item lg={12}>
              <Button variant={'contained'} type={'submit'} disabled={isUpsertingOrganisation} sx={{ mr: 3 }}>
                {selectedOrganisationId ? 'Update ' : 'Create '} Organisation
                {isUpsertingOrganisation && <ButtonLoadingComponent />}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Paper>
  );
};

export default UpsertOrganisationComponent;
